/**
 * MobileLeagueTradingBlock — CD delivery `Trading Block.dc.html` (CLAUDE.md §132-135
 * "LOCKED — Trading Block page"). LeagueChrome (active trading-block, parent) → header
 * row (title + gold count badge + "+ Add") → team-grouped FLUSH cards (screenTop fill,
 * 1px border, NO curve), your team first with a 2px gold left-rule + "(You)", other
 * teams a rotating accent on the team name → docked BottomNav. Each item row = POS badge
 * (gold) or PK badge (purple) · name · salary/contract meta (picks show year·round +
 * original owner); remove ✕ (red) ONLY on your items; italic team note at the card foot.
 * "+ Add" = a bottom sheet (Player / Draft Pick tabs, selectable list with gold ✓ + tint,
 * optional comment, Cancel / Add to Block).
 *
 * RESTYLE (this pass): UI conformed to the mock. KEPT verbatim (Rule 11): the data layer
 * — getTradingBlock / removeFromTradingBlock / addToTradingBlock + the add sheet's
 * getMyRosterCanonical / getTeamDraftPicks loads, the grouping (your team first), the
 * on-block exclusion, and the add payload shape. Tokens only, zero hex; flush, no rounded
 * cards (the bottom-sheet top radius is the sheet affordance, not a content card);
 * scrollbars hidden.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { dynastyTheme, withAlpha } from '../../../services/colorService';
import { leaguesAPI } from '../../../services/apiService';
import { MobilePlayerModal, MobilePlayerCard } from '../components/player';

const C = dynastyTheme.tokens.colors;

// Rotating team-name accent for non-your teams. Distinct accent TOKENS (the mock's
// illustrative palette maps to these; the rotation is decorative, not a locked hue).
const TEAM_ACCENTS = [C.blue, C.purpleLight, C.orange.soft, C.spark.up, C.purpleLighter, C.warning, C.spark.down, C.info];

const GOLD_SOFT = withAlpha(C.goldBase, 0.13); // tp-soft
const GOLD_LINE = withAlpha(C.goldBase, 0.32); // tp-line
const GOLD_FAINT = withAlpha(C.goldBase, 0.08);

function MobileLeagueTradingBlock({ leagueId, currentUserTeamId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [removing, setRemoving] = useState(null);
  const scrollRef = useRef(null);
  const [scrollHeight, setScrollHeight] = useState(400);

  // Player modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCardOpen, setIsCardOpen] = useState(false);
  const [activePlayerId, setActivePlayerId] = useState(null);

  const fetchBlock = useCallback(async () => {
    if (!leagueId) return;
    try {
      const res = await leaguesAPI.getTradingBlock(leagueId);
      setItems(res?.players || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => { fetchBlock(); }, [fetchBlock]);

  // Measure scrollable area (header fixed, cards scroll — mock's flex:1 list).
  useEffect(() => {
    const measure = () => {
      if (scrollRef.current) {
        const rect = scrollRef.current.getBoundingClientRect();
        setScrollHeight(Math.max(200, window.innerHeight - rect.top - 80));
      }
    };
    const timer = setTimeout(measure, 200);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(timer); window.removeEventListener('resize', measure); };
  }, [loading]);

  // Group items by team, your team first.
  const grouped = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      const key = item.team_id || 'unknown';
      if (!map[key]) map[key] = { team_id: item.team_id, team_name: item.team_name || 'Unknown', items: [] };
      map[key].items.push(item);
    });
    const groups = Object.values(map);
    groups.sort((a, b) => {
      const aYours = a.team_id === currentUserTeamId ? -1 : 0;
      const bYours = b.team_id === currentUserTeamId ? -1 : 0;
      if (aYours !== bYours) return aYours - bYours;
      return (a.team_name || '').localeCompare(b.team_name || '');
    });
    return groups;
  }, [items, currentUserTeamId]);

  const handleRemove = async (blockId) => {
    setRemoving(blockId);
    try {
      await leaguesAPI.removeFromTradingBlock(leagueId, blockId);
      await fetchBlock();
    } catch (err) {
      console.error('Failed to remove:', err);
    } finally {
      setRemoving(null);
    }
  };

  const handlePlayerClick = useCallback((mlbId) => {
    if (mlbId) { setActivePlayerId(mlbId.toString()); setIsModalOpen(true); }
  }, []);
  const handleModalClose = () => { setIsModalOpen(false); setTimeout(() => setActivePlayerId(null), 300); };
  const handleExpandToCard = () => { setIsModalOpen(false); setIsCardOpen(true); };
  const handleCollapseToModal = () => { setIsCardOpen(false); setIsModalOpen(true); };
  const handleCloseAll = () => { setIsCardOpen(false); setIsModalOpen(false); setTimeout(() => setActivePlayerId(null), 300); };

  const totalCount = items.length;

  const addBtn = (big) => (
    <button onClick={() => setShowAddModal(true)} style={{
      border: `1px solid ${GOLD_LINE}`, color: C.primary, background: GOLD_FAINT, borderRadius: 4,
      fontSize: big ? 11 : 11, fontWeight: 700, padding: big ? '5px 14px' : '5px 11px', cursor: 'pointer',
    }}>+ Add</button>
  );

  return (
    <div>
      <style>{`.tb-noscroll::-webkit-scrollbar{display:none;}`}</style>

      {/* Header row — title + gold count badge + + Add */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderBottom: `1px solid ${C.border}` }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontSize: 14, fontWeight: 900, color: C.textHi, whiteSpace: 'nowrap' }}>Trading Block</span>
          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: GOLD_SOFT, color: C.primary }}>{totalCount}</span>
        </span>
        {addBtn(false)}
      </div>

      {/* Team cards */}
      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: C.muted, fontSize: 12 }}>Loading trading block…</div>
      ) : totalCount === 0 ? (
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ color: C.muted, fontSize: 12, marginBottom: 12 }}>No players or picks on the trading block yet</div>
          {addBtn(true)}
        </div>
      ) : (
        <div ref={scrollRef} className="tb-noscroll" style={{ maxHeight: scrollHeight, overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', padding: '7px 8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {grouped.map((group, gi) => {
            const isYours = group.team_id === currentUserTeamId;
            // rotating accent indexed by non-your appearance order (stable per render)
            const teamColor = isYours ? C.primary : TEAM_ACCENTS[gi % TEAM_ACCENTS.length];
            const uniqueNote = [...new Set(group.items.map((i) => i.note).filter(Boolean))].join(' · ');
            return (
              <div key={group.team_id} style={{
                background: C.ground.screenTop, border: `1px solid ${C.border}`, padding: '8px 11px',
                ...(isYours ? { borderLeft: `2px solid ${C.primary}` } : null),
              }}>
                {/* Team header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: teamColor }}>{group.team_name}{isYours ? ' (You)' : ''}</span>
                  <span style={{ fontSize: 9, color: C.muted }}>{group.items.length} item{group.items.length !== 1 ? 's' : ''}</span>
                </div>

                {/* Items */}
                {group.items.map((item, idx) => {
                  const isPick = item.item_type === 'pick';
                  return (
                    <div key={item.block_id || idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderTop: `1px solid ${C.surface[880]}` }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: isPick ? C.purpleLighter : C.primary, width: 24, flexShrink: 0, textAlign: 'center' }}>
                        {isPick ? 'PK' : (item.position || '?')}
                      </span>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 5 }}>
                        {isPick ? (
                          <span style={{ fontSize: 12, fontWeight: 600, color: C.textHi, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.pick_year || '?'} R{item.pick_round || '?'}{item.pick_original_owner ? ` (${item.pick_original_owner}'s)` : ''}
                          </span>
                        ) : (
                          <>
                            <span onClick={() => handlePlayerClick(item.mlb_id)} style={{ fontSize: 12, fontWeight: 600, color: C.textHi, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.player_name || 'Unknown'}</span>
                            {(item.salary != null || item.contract_type) && (
                              <span style={{ fontSize: 9, color: C.muted, flexShrink: 0 }}>{item.salary != null ? `$${item.salary}` : ''}{item.contract_type ? `/${item.contract_type}` : ''}</span>
                            )}
                          </>
                        )}
                      </div>
                      {isYours && (
                        <span onClick={() => removing !== item.block_id && handleRemove(item.block_id)} style={{ color: C.error, fontSize: 12, fontWeight: 700, opacity: removing === item.block_id ? 0.4 : 0.7, cursor: 'pointer', flexShrink: 0 }}>
                          {removing === item.block_id ? '…' : '✕'}
                        </span>
                      )}
                    </div>
                  );
                })}

                {/* Team note */}
                {uniqueNote && (
                  <div style={{ fontSize: 9, color: C.neutral[400], fontStyle: 'italic', paddingTop: 5, marginTop: 3, borderTop: `1px solid ${C.surface[880]}` }}>"{uniqueNote}"</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add sheet */}
      {showAddModal && (
        <AddSheet
          leagueId={leagueId}
          currentUserTeamId={currentUserTeamId}
          existingItems={items}
          onClose={() => setShowAddModal(false)}
          onAdded={() => { setShowAddModal(false); fetchBlock(); }}
        />
      )}

      {/* Player modal / card */}
      <MobilePlayerModal playerId={activePlayerId} isOpen={isModalOpen} onClose={handleModalClose} onExpandToCard={handleExpandToCard} leagueId={leagueId} />
      <MobilePlayerCard playerId={activePlayerId} leagueId={leagueId} isOpen={isCardOpen} onClose={handleCloseAll} onCollapse={handleCollapseToModal} />
    </div>
  );
}

// ============================================================================
// ADD SHEET — bottom sheet, Player / Draft Pick tabs, selectable list (gold ✓ + tint)
// ============================================================================
function AddSheet({ leagueId, currentUserTeamId, existingItems, onClose, onAdded }) {
  const [tab, setTab] = useState('player');
  const [roster, setRoster] = useState([]);
  const [picks, setPicks] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(true);
  const [loadingPicks, setLoadingPicks] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const existingPlayerIds = useMemo(() =>
    new Set(existingItems.filter((i) => i.item_type !== 'pick' && i.league_player_id).map((i) => i.league_player_id)), [existingItems]);
  const existingPickIds = useMemo(() =>
    new Set(existingItems.filter((i) => i.item_type === 'pick' && i.pick_id).map((i) => i.pick_id)), [existingItems]);

  useEffect(() => {
    leaguesAPI.getMyRosterCanonical(leagueId)
      .then((res) => setRoster((res?.players || []).filter((p) => !p.accrued_info)))
      .catch(() => setRoster([]))
      .finally(() => setLoadingRoster(false));
    leaguesAPI.getTeamDraftPicks(leagueId, currentUserTeamId, true) // §4.5.24 tradeable only
      .then((res) => { const all = res?.picks || res?.data || []; setPicks(Array.isArray(all) ? all : []); })
      .catch(() => setPicks([]))
      .finally(() => setLoadingPicks(false));
  }, [leagueId, currentUserTeamId]);

  const availablePlayers = roster.filter((p) => { const id = p.league_player_id || p.id; return id && !existingPlayerIds.has(id); });
  const availablePicks = picks.filter((pk) => { const id = pk.pick_id || pk.id; return id && !existingPickIds.has(id); });

  const handleSubmit = async () => {
    if (!selectedId) { setError('Select an item'); return; }
    setSubmitting(true);
    setError('');
    try {
      const payload = { note, item_type: tab };
      if (tab === 'player') {
        payload.league_player_id = selectedId;
      } else {
        payload.pick_id = selectedId;
        const pick = picks.find((pk) => (pk.pick_id || pk.id) === selectedId);
        payload.pick_type = pick?.pick_type || 'regular';
      }
      await leaguesAPI.addToTradingBlock(leagueId, payload);
      onAdded();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to add');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => { setSelectedId(''); setError(''); }, [tab]);

  // Selectable rows for the active tab.
  const loadingList = tab === 'player' ? loadingRoster : loadingPicks;
  const rows = tab === 'player'
    ? availablePlayers.map((p) => ({
        id: p.league_player_id || p.id,
        badge: p.info?.primary_position || p.position || '—', badgeCol: C.primary,
        name: p.info?.full_name || p.player_name || 'Unknown',
        meta: p.league_context?.financial?.contract_salary != null ? `$${p.league_context.financial.contract_salary}` : '',
      }))
    : availablePicks.map((pk) => ({
        id: pk.pick_id || pk.id,
        badge: 'PK', badgeCol: C.purpleLighter,
        name: `${pk.year || pk.pick_year || '?'} R${pk.round || pk.pick_round || '?'}${pk.pick_type === 'ml' ? ' (ML)' : ''}`,
        meta: pk.original_owner_name ? `${pk.original_owner_name}'s` : 'your pick',
      }));

  const tabStyle = (active) => ({
    flex: 1, textAlign: 'center', padding: '6px 0', fontSize: 11, fontWeight: 700, cursor: 'pointer',
    color: active ? C.primary : C.muted, borderBottom: `2px solid ${active ? C.primary : 'transparent'}`,
  });

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9999, background: withAlpha(C.black, 0.6), display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', background: `linear-gradient(180deg, ${C.ground.screenTop}, ${C.ground.screen})`, borderTop: `1px solid ${C.border}`, borderRadius: '16px 16px 0 0', paddingBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '7px 0 5px' }}><div style={{ width: 36, height: 3, borderRadius: 2, background: C.coolGray[720] }} /></div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px 9px' }}>
          <span style={{ fontSize: 14, fontWeight: 900, color: C.textHi }}>Add to Trading Block</span>
          <span onClick={onClose} style={{ fontSize: 13, color: C.muted, cursor: 'pointer' }}>✕</span>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, margin: '0 14px' }}>
          <div onClick={() => setTab('player')} style={tabStyle(tab === 'player')}>Player</div>
          <div onClick={() => setTab('pick')} style={tabStyle(tab === 'pick')}>Draft Pick</div>
        </div>

        <div style={{ padding: '11px 14px' }}>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: C.primary, marginBottom: 6 }}>
            {tab === 'player' ? 'Your roster' : 'Your draft picks'}
          </div>
          {/* Selectable list */}
          <div className="tb-noscroll" style={{ maxHeight: 200, overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {loadingList ? (
              <div style={{ fontSize: 11, color: C.muted, padding: '8px 0' }}>Loading…</div>
            ) : rows.length === 0 ? (
              <div style={{ fontSize: 11, color: C.muted, padding: '8px 0' }}>Nothing available to add.</div>
            ) : rows.map((r) => {
              const on = selectedId === r.id;
              return (
                <div key={r.id} onClick={() => setSelectedId(r.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px', borderRadius: 4, cursor: 'pointer',
                  background: on ? GOLD_SOFT : C.ground.screenTop, border: `1px solid ${on ? GOLD_LINE : C.border}`,
                }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: r.badgeCol, width: 22, textAlign: 'center', flexShrink: 0 }}>{r.badge}</span>
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: C.textHi, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
                  {r.meta && <span style={{ fontSize: 9, color: C.muted, flexShrink: 0 }}>{r.meta}</span>}
                  <span style={{ fontSize: 12, color: C.primary, flexShrink: 0, width: 12, textAlign: 'center' }}>{on ? '✓' : ''}</span>
                </div>
              );
            })}
          </div>

          {/* Optional comment */}
          <textarea
            placeholder="Optional comment (300 max)…" value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} maxLength={300} rows={2}
            style={{ width: '100%', boxSizing: 'border-box', marginTop: 9, background: C.ground.screen, border: `1px solid ${C.border}`, borderRadius: 4, padding: '8px 10px', fontSize: 11, color: C.textHi, resize: 'none', fontFamily: 'inherit', outline: 'none' }}
          />
          {error && <div style={{ fontSize: 10, color: C.error, marginTop: 6 }}>{error}</div>}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={onClose} style={{ flex: 1, textAlign: 'center', padding: '9px 0', background: C.surface[850], color: C.muted, border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={submitting || !selectedId} style={{
              flex: 1, textAlign: 'center', padding: '9px 0', borderRadius: 4, fontSize: 11, fontWeight: 800, border: 'none',
              background: C.primary, color: C.neutral[950], cursor: (submitting || !selectedId) ? 'default' : 'pointer', opacity: (submitting || !selectedId) ? 0.45 : 1,
            }}>{submitting ? 'Adding…' : 'Add to Block'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MobileLeagueTradingBlock;
